'use client';

import { atom, useAtom } from 'jotai';
import { AnimatePresence } from 'motion/react';
import * as m from 'motion/react-m';
import { useEffect } from 'react';
import { createPortal } from 'react-dom';

import { useBodyClass } from '@flipster/react/misc/browser/body-class';
import { useMounted } from '@flipster/react/misc/lifecycle/mount';
import { useResponsive } from '@flipster/react/misc/responsive/legacy';
import { AlertProps, alertListeners } from '@flipster/utils/alert';

import { Button } from '../Button';
import { Space } from '../Space';
import ExclamationCircleIcon from '../assets/svgs/filled/exclamation-circle.svg';
import styles from './alert.module.scss';

const alertAtom = atom<AlertProps | null>(null);
alertAtom.debugLabel = 'alertAtom';

const Alert = () => {
  const [alert, setAlert] = useAtom(alertAtom);

  const isMounted = useMounted();
  const { isDesktop } = useResponsive();

  useEffect(() => {
    alertListeners.add(setAlert);
    return () => {
      alertListeners.delete(setAlert);
    };
  }, [setAlert]);

  useBodyClass('alert', !!alert);

  const onConfirm = () => {
    alert?.onConfirm?.();
    setAlert(null);
  };

  const onClose = () => {
    alert?.onClose?.();
    setAlert(null);
  };

  if (!isMounted) return null;

  const alertRoot = document.querySelector('#alert');
  if (!alertRoot) return null;

  return createPortal(
    <AnimatePresence initial={false}>
      {alert && (
        <aside className={styles.wrapper}>
          <m.div
            className={styles.alert}
            initial={{ opacity: 0.5, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1, transition: { ease: 'easeOut', duration: 0.2 } }}
            exit={{ opacity: 0.5, scale: 0.8, transition: { ease: 'easeIn', duration: 0.2 } }}
            layout
          >
            {alert.title && alert.warn ? (
              <h1 className={styles.warn}>
                <ExclamationCircleIcon className={styles.warnIcon} />
                {alert.title}
              </h1>
            ) : (
              <h1>{alert.title}</h1>
            )}
            <div className={styles.content}>{alert.message}</div>
            <Space className={styles.buttons} center isFull={!isDesktop}>
              {alert.withClose && (
                <Button shape='tertiary' onClick={onClose} isFull={!isDesktop} size='medium'>
                  {alert.closeText ?? 'Close'}
                </Button>
              )}
              <Button shape='primary' onClick={onConfirm} isFull={!isDesktop} size='medium'>
                {alert.confirmText ?? 'Confirm'}
              </Button>
            </Space>
          </m.div>
        </aside>
      )}
    </AnimatePresence>,
    alertRoot,
  );
};

export default Alert;
